import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import "@fontsource/fraunces/600.css";
import "@fontsource/noto-serif/400.css";
import "@fontsource/noto-serif/400-italic.css";
import "@fontsource/noto-serif/600.css";
import { App } from "./App";
import { ApplicationErrorBoundary, reloadForNewBuild } from "./shared/ApplicationErrorBoundary";
import { AudioExperienceProvider } from "./shared/AudioExperience";
import { AuthSessionProvider } from "./shared/AuthSession";
import { reportClientError } from "./shared/customer-activity";
import "./app.css";
import "./vc-demo-accessibility.css";

// A tab opened before a deploy asks for files the deploy replaced; it reloads once to the current
// release instead of breaking the next screen (end-to-end run 8, 2026-10-15). The error still
// reaches the page, which says it is loading the latest version while the reload is on its way:
// swallowing it would hand the screen an empty module (review, 2026-10-15).
window.addEventListener("vite:preloadError", () => {
  reloadForNewBuild();
});

// A failure the page didn't handle reaches the team as a code (pilot watch, 2026-10-17). Another
// site's script, a browser extension or a request the page cancelled is not ours to report.
window.addEventListener("error", (event) => {
  if (
    event.error instanceof Error &&
    (!event.filename || event.filename.startsWith(window.location.origin))
  )
    reportClientError("UNHANDLED_ERROR");
});
window.addEventListener("unhandledrejection", (event) => {
  if (event.reason instanceof Error && event.reason.name === "AbortError") return;
  reportClientError("UNHANDLED_REJECTION");
});

const root = document.getElementById("root");
if (!root) throw new Error("Application root is missing");

createRoot(root).render(
  <StrictMode>
    <AuthSessionProvider>
      <AudioExperienceProvider>
        <BrowserRouter>
          <ApplicationErrorBoundary>
            <App />
          </ApplicationErrorBoundary>
        </BrowserRouter>
      </AudioExperienceProvider>
    </AuthSessionProvider>
  </StrictMode>,
);
